import { matchesRouterPath } from "@/app/router/router-path";

/**
 * Static public navigation ported from the legacy site (011-CLMS-241 R9, FR-015): the shared
 * header dropdowns (`_HeaderMenu.cshtml`) and the Home side menus (`HomeController.cs`
 * `SetSubLinksAndTitle`), in legacy order and wording. Only entries whose target page exists in
 * clm-web are listed; later content pages add their entries here.
 */
export interface PublicNavEntry {
  label: string;
  href: string;
  /** A destination outside clm-web: a plain link that opens in a new tab, never the current page. */
  external?: true;
}

// External destinations of the legacy menus (_HeaderMenu.cshtml, HomeController SetSubLinksAndTitle).
const TASK_FORCE: PublicNavEntry = {
  label: "Litigation Management Task Force",
  href: "https://theclm87249.ac-page.com/clm-litigation-management-task-force-structure",
  external: true,
};
const SUBMIT_MEMBERSHIP_NEWS: PublicNavEntry = {
  label: "Submit Membership News",
  href: "https://airtable.com/appZDtzlHi9Bq9axu/pagzw7NGN5YI3hFnR/form",
  external: true,
};
const MAGAZINE_WEBSITE: PublicNavEntry = {
  label: "Magazine Website",
  href: "https://www.theclm.org/Magazine/",
  external: true,
};

export interface PublicHeaderGroup {
  /** Top-level label in sentence case (DES-1); the legacy header uppercased it in CSS. */
  label: string;
  /** Landing page of the top-level label. */
  href: string;
  entries: readonly PublicNavEntry[];
}

export interface PublicSideMenu {
  label: string;
  entries: readonly PublicNavEntry[];
}

export const PUBLIC_HEADER_MENU: readonly PublicHeaderGroup[] = [
  {
    label: "About",
    href: "/home/aboutus",
    entries: [
      { label: "About", href: "/home/aboutus" },
      { label: "Contact", href: "/home/contactus" },
    ],
  },
  // The label opens Communities, as the legacy header's did (T037 D01).
  {
    label: "Our network",
    href: "/home/committeelist",
    entries: [
      { label: "Leadership", href: "/home/leadership" },
      { label: "Communities", href: "/home/committeelist" },
      { label: "Local Chapters", href: "/home/localchapters" },
    ],
  },
  {
    label: "Membership",
    href: "/home/membershiptypes",
    entries: [
      { label: "Types of Membership", href: "/home/membershiptypes" },
      { label: "Insurance Professional", href: "/home/fellows" },
      { label: "Outside Counsel", href: "/home/members" },
      { label: "Service Provider", href: "/home/mediatororarbitratormembers" },
      { label: "Corporate Fellows", href: "/home/corporatemembers" },
    ],
  },
  {
    label: "Resources",
    href: "/home/resources",
    entries: [
      { label: "CLM Tracker", href: "/home/clmtracker" },
      { label: "Designations", href: "/home/designations" },
      TASK_FORCE,
      { label: "E-Courses", href: "/home/resourceecourses" },
      { label: "Magazines", href: "/home/clmpublishing" },
      MAGAZINE_WEBSITE,
      { label: "Media Kits", href: "/home/mediakitandsponsorships" },
      { label: "Sponsorship Opportunities", href: "/home/conferencesponsorshipopportunities" },
      { label: "Dispute Resolution", href: "/home/disputeresolution" },
      SUBMIT_MEMBERSHIP_NEWS,
      { label: "Professional of the Year", href: "/home/clmprofessionals" },
    ],
  },
  // The legacy label links to All Events. Its Calendar entry and the dynamic event list are not
  // ported (the key register's "Menu entries left out"), so only the fixed entries show.
  {
    label: "Events",
    href: "/home/allevents",
    entries: [
      { label: "In person", href: "/home/inpersonevents" },
      { label: "Virtual", href: "/home/virtualevents" },
    ],
  },
];

export const PUBLIC_SIDE_MENUS = {
  about: {
    label: "About",
    entries: [
      { label: "About", href: "/home/aboutus" },
      { label: "Contact", href: "/home/contactus" },
    ],
  },
  membership: {
    label: "Membership",
    entries: [
      { label: "Types Of Membership", href: "/home/membershiptypes" },
      { label: "Insurance Professional", href: "/home/fellows" },
      { label: "Outside Counsel", href: "/home/members" },
      { label: "Service Provider", href: "/home/mediatororarbitratormembers" },
      { label: "Corporate Fellows", href: "/home/corporatemembers" },
    ],
  },
  // CLM Magazine, Construction Claims Magazine and Job Search are left out (CLMS-238, no page).
  resources: {
    label: "Resources",
    entries: [
      { label: "CLM Tracker", href: "/home/clmtracker" },
      { label: "Designations", href: "/home/designations" },
      TASK_FORCE,
      { label: "E-Courses", href: "/home/resourceecourses" },
      { label: "Media Kits", href: "/home/mediakitandsponsorships" },
      { label: "Sponsorship Opportunities", href: "/home/conferencesponsorshipopportunities" },
      { label: "Dispute Resolution", href: "/home/disputeresolution" },
      SUBMIT_MEMBERSHIP_NEWS,
      { label: "Professional of the Year", href: "/home/clmprofessionals" },
    ],
  },
  // Only Communities and Local Chapters have pages; the legacy menu also led to the member directory,
  // the advisory boards and the industry resource directory (other stories).
  network: {
    label: "Our network",
    entries: [
      { label: "Communities", href: "/home/committeelist" },
      { label: "Local Chapters", href: "/home/localchapters" },
    ],
  },
  // CLM Magazine and Construction Claims lead the legacy menu (CLMS-238, no page yet).
  publishing: {
    label: "CLM Publishing",
    entries: [{ label: "Media Kits", href: "/home/mediakitandsponsorships" }],
  },
  // The menu of the Sponsorship Opportunities page; Job Search, the two magazines and the podcast
  // are left out, and so is CLM Advisors (its address does not resolve).
  sponsorship: {
    label: "Sponsorship Opportunities",
    entries: [
      { label: "Professional Times Magazine", href: "/home/professionaltimes" },
      { label: "Media Kits", href: "/home/mediakitandsponsorships" },
      { label: "Sponsorship Opportunities", href: "/home/sponsorshipopportunities" },
      { label: "Dispute Resolution", href: "/home/disputeresolution" },
      { label: "Travel Grants", href: "/home/clmscholarship" },
      { label: "Digital Badge Program", href: "/home/digitalbadgeprogram" },
      { label: "CLM Tracker", href: "/home/clmtracker" },
    ],
  },
  // Claims College and its four schools are microsite data (CLMS-243), so only the two copy pages stay.
  claimsCollege: {
    label: "Claims College",
    entries: [
      { label: "Faculty Application", href: "/home/facultyapplication" },
      { label: "Sponsorship Opportunities", href: "/home/sponsorshipopportunities" },
    ],
  },
  // The five links HomeController and EventController gave every events page; Calendar is not ported.
  events: {
    label: "Events",
    entries: [
      { label: "All Events", href: "/home/allevents" },
      { label: "In Person", href: "/home/inpersonevents" },
      { label: "Virtual", href: "/home/virtualevents" },
      { label: "Instructor Sign Up", href: "/home/instructorsignup" },
    ],
  },
} as const satisfies Record<string, PublicSideMenu>;

export type PublicSideMenuId = keyof typeof PUBLIC_SIDE_MENUS;

/** Whether `pathname` is the page at `href`, read as the router reads it (decoded, any letter case). */
export function isActivePublicPath(pathname: string, href: string): boolean {
  return matchesRouterPath(href, pathname);
}
